import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppDispatch } from '@/renderer/hooks/useRedux';
import { queryKeys } from '@lib/queryKeys';
import * as accommodationService from '@/renderer/services/accommodationService';
import { updatePropertyThunk } from '@/renderer/store/accommodation/accommodationThunks';
import { supabase } from '@/renderer/services/supabase';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import type {
  AccommodationBookingStatus,
  CreateBookingRequest,
  CreatePaymentRequest,
  CreateRatePlanRequest,
  CreateRoomRequest,
  CreateRoomTypeRequest,
  UpdateBookingRequest,
  UpdatePropertyRequest,
  UpdateRatePlanRequest,
  UpdateRoomRequest,
  UpdateRoomTypeRequest,
  EarlyCheckoutParams,
} from '@/renderer/types/accommodation';

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    queryKey: queryKeys.accommodation.all,
  });
}

// ── Property ──────────────────────────────────────────────────────────────────

export function useUpdateProperty() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  return useMutation({
    // Route through the Redux thunk so Redux `property` state stays current
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdatePropertyRequest;
    }) => dispatch(updatePropertyThunk({ id, updates })).unwrap(),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Property updated.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Room Types ────────────────────────────────────────────────────────────────

export function useCreateRoomType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateRoomTypeRequest) =>
      accommodationService.createRoomType(request),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Room type created.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateRoomType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateRoomTypeRequest;
    }) => accommodationService.updateRoomType(id, updates),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Room type updated.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Rooms ─────────────────────────────────────────────────────────────────────

export function useCreateRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateRoomRequest) =>
      accommodationService.createRoom(request),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Room created.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateRoom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateRoomRequest }) =>
      accommodationService.updateRoom(id, updates),
    onSuccess: () => {
      invalidateAll(queryClient);
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Rate Plans ────────────────────────────────────────────────────────────────

export function useCreateRatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateRatePlanRequest) =>
      accommodationService.createRatePlan(request),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Rate plan created.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateRatePlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateRatePlanRequest;
    }) => accommodationService.updateRatePlan(id, updates),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Rate plan updated.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Bookings ──────────────────────────────────────────────────────────────────

export function useCreateBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateBookingRequest) =>
      accommodationService.createBooking(request),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Booking created.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      notes,
    }: {
      id: string;
      status: AccommodationBookingStatus;
      notes?: string;
    }) => {
      const booking = await accommodationService.updateBookingStatus(
        id,
        status,
        notes,
      );

      if (status !== 'no_show') {
        supabase.functions
          .invoke('send-booking-email', {
            body: { booking_id: id, event_type: status },
          })
          .catch(() => {});
      }

      return booking;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateBookingRequest;
    }) => accommodationService.updateBooking(id, updates),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Booking updated.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Payments ──────────────────────────────────────────────────────────────────

export function useRecordPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreatePaymentRequest) =>
      accommodationService.recordBookingPayment(request),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Payment recorded.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdatePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentId,
      updates,
      reason,
    }: {
      paymentId: string;
      updates: {
        amount: number;
        payment_method_id: number;
        paid_at: string;
        notes?: string | null;
      };
      reason: string;
    }) => accommodationService.updateBookingPayment(paymentId, updates, reason),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Payment updated. The change has been logged.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useDeletePayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentId,
      reason,
    }: {
      paymentId: string;
      reason: string;
    }) => accommodationService.deleteBookingPayment(paymentId, reason),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Payment deleted. The change has been logged.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

export function useUpdateBookingRoomRates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      bookingId,
      rates,
      reason,
    }: {
      bookingId: string;
      rates: { booking_room_id: string; price_per_night: number }[];
      reason: string;
    }) => accommodationService.updateBookingRoomRates(bookingId, rates, reason),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Room rates updated. The change has been logged.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Guest / client (accommodation profile) ──────────────────────────────────────

export function useUpdateAccommodationClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Parameters<
        typeof accommodationService.updateClientAccommodationDetails
      >[1];
    }) => accommodationService.updateClientAccommodationDetails(id, updates),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Guest details updated.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}

// ── Early Checkout ───────────────────────────────────────────────────────────

export function useEarlyCheckout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: EarlyCheckoutParams) =>
      accommodationService.performEarlyCheckout(params),
    onSuccess: () => {
      invalidateAll(queryClient);
      apiSuccess('Early checkout completed successfully.');
    },
    onError: (err: Error) => apiError(err.message),
  });
}
